import type {
  LookupItem,
  PublicFaqEntry,
  PublicLeadAck,
  PublicLeadRequest,
  PublicParentOpinion,
} from "@fa/shared";
import { API_BASE_URL } from "@/lib/env";

export interface PublicApiErrorDetail {
  field?: string;
  message: string;
}

/**
 * Normalized error thrown by every function in this module — mirrors the API's single error
 * envelope (07-cross-cutting.md §5 / `ErrorEnvelope`), same shape apps/web's `ApiError` reads,
 * just built on `fetch` instead of axios (this app has no HTTP client dependency otherwise, and
 * the public site only ever makes these three unauthenticated GET/POST calls).
 */
export class PublicApiError extends Error {
  readonly code: string;
  /** `null` for a network-level failure (no response reached at all). */
  readonly status: number | null;
  readonly details: PublicApiErrorDetail[];

  constructor(params: {
    code: string;
    message: string;
    status: number | null;
    details?: PublicApiErrorDetail[];
  }) {
    super(params.message);
    this.name = "PublicApiError";
    this.code = params.code;
    this.status = params.status;
    this.details = params.details ?? [];
  }
}

interface ErrorEnvelopeBody {
  error?: {
    code?: string;
    message?: string;
    details?: PublicApiErrorDetail[];
  };
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      ...init,
      headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
    });
  } catch {
    throw new PublicApiError({
      code: "NETWORK_ERROR",
      message:
        "Could not reach the server. Please check your connection and try again.",
      status: null,
    });
  }

  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    // No/invalid JSON body (e.g. a 204 or an upstream proxy error page) — fall through, handled
    // below by status.
  }

  if (!response.ok) {
    const envelope = (body ?? {}) as ErrorEnvelopeBody;
    throw new PublicApiError({
      code: envelope.error?.code ?? "INTERNAL_ERROR",
      message: envelope.error?.message ?? `Request failed (${response.status})`,
      status: response.status,
      details: envelope.error?.details,
    });
  }

  return body as T;
}

/** `GET /public/lookups/grades` — active grades only, unauthenticated. */
export function getPublicGrades(): Promise<LookupItem[]> {
  return request<LookupItem[]>("/public/lookups/grades");
}

/** `GET /public/lookups/schools` — active agreed schools only, unauthenticated. */
export function getPublicSchools(): Promise<LookupItem[]> {
  return request<LookupItem[]>("/public/lookups/schools");
}

/**
 * `GET /public/content/parent-opinions` — active parents' opinions only, unauthenticated, ordered
 * by `sortOrder` ascending, both languages per row (T139/T141 — `PublicParentOpinion` is the
 * both-language public projection; `ParentsOpinions.tsx` picks the active `lang` client-side).
 */
export function getPublicParentOpinions(): Promise<PublicParentOpinion[]> {
  return request<PublicParentOpinion[]>("/public/content/parent-opinions");
}

/**
 * `GET /public/content/faq` — active FAQ entries only, unauthenticated, ordered by `sortOrder`
 * ascending, both languages per row (T139/T141 — `Faq.tsx` picks the active `lang` client-side).
 */
export function getPublicFaq(): Promise<PublicFaqEntry[]> {
  return request<PublicFaqEntry[]>("/public/content/faq");
}

/** `POST /public/leads` — unauthenticated lead-capture submission (T25). */
export function submitPublicLead(
  payload: PublicLeadRequest,
): Promise<PublicLeadAck> {
  return request<PublicLeadAck>("/public/leads", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}
